import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { Alignment, Fit, Layout, useRive } from 'rive-react';

import Button from 'components/shared/button/button';
import Link from 'components/shared/link/link';

const riveAnimations = {
  'customer-center': {
    animation: '/animations/customer-center/hero.riv',
    className:
      '-top-3.5 -right-[71px] w-auto h-[547px] aspect-[498/478] xl:-top-px xl:right-px xl:h-[518px] lg:h-[528px] md:top-2 md:h-[360px]',
    sectionClassName: 'pt-[133px] pb-[114px] xl:pt-16 xl:pb-9 lg:pb-[30px] md:pb-[60px]',
  },
  'for-startups': {
    animation: '/animations/for-startups/hero.riv',
    className:
      'relative -top-2.5 -right-[50px] [&>*]:min-w-[676px] [&>*]:min-h-[758px] 2xl:right-[-24px] xl:-top-[5px] xl:-right-[30px] xl:[&>*]:min-w-[500px] xl:[&>*]:min-h-[560px] lg:top-0 lg:right-0 lg:[&>*]:min-w-[676px] lg:[&>*]:min-h-[758px] md:[&>*]:min-w-[460px] md:[&>*]:min-h-[516px] sm:[&>*]:min-w-[400px] sm:[&>*]:min-h-[449px] xs:[&>*]:min-w-[300px] xs:[&>*]:min-h-[336px] xs:mt-6',
    sectionClassName: 'pb-5 lg:pt-10',
  },
};

const HeroWithTagAndAnimation = ({
  tag,
  title,
  description,
  buttonText,
  buttonUrl,
  linkText,
  linkUrl,
  animationName,
}) => {
  const { animation, className, sectionClassName } = riveAnimations[animationName];

  const { RiveComponent, riveWrapperRef } = useRive({
    src: animation,
    stateMachines: 'SM',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  return (
    <section
      className={clsx(
        'customer-center-hero safe-paddings bg-new-gray-2 overflow-hidden',
        sectionClassName
      )}
    >
      <div className="container grid grid-cols-12 items-center gap-y-14">
        <div className="col-span-6 -mt-[95px] xl:col-start-3 xl:col-span-8 xl:mt-0 xl:text-center lg:col-start-2 lg:col-span-10 sm:col-start-1 sm:col-span-full">
          <span className="fs-16 flex items-center w-max h-[38px] mb-4 pt-[3px] px-4 font-medium -tracking-tight-4 text-secondary-2 rounded-full border-2 border-secondary-2 -translate-y-0.5 xl:-mt-1 xl:mx-auto xl:mb-7 xl:translate-y-0 lg:mb-6 md:mb-5 md:text-[16px]">
            {tag}
          </span>
          <h1 className="fs-56 pr-[72px] leading-tight -tracking-[0.03em] text-new-black xl:pr-0 xl:leading-denser xl:tracking-tight-4 lg:px-[60px] md:px-8">
            {title}
          </h1>
          <p className="fs-20 mt-3 pr-28 -tracking-tight-2 text-new-gray-1 xl:mt-4 xl:px-7 xl:text-[20px] lg:px-3 lg:text-[18px] md:mt-3 md:text-[16px]">
            {description}
          </p>
          <div className="flex gap-8 mt-10 xl:justify-center xl:mt-8 lg:gap-6 lg:mt-6 md:mt-9 xs:gap-3">
            <Button
              className="!font-medium !text-[16px] !tracking-tight-2 whitespace-nowrap"
              theme="secondary-2"
              size="medium"
              to={buttonUrl}
            >
              {buttonText}
            </Button>
            <Link
              className="whitespace-nowrap xl:text-[16px]"
              theme="secondary-2"
              size="md"
              to={linkUrl}
              withArrow
            >
              {linkText}
            </Link>
          </div>
        </div>
        <div
          className="relative col-span-6 flex justify-center items-center min-h-full xl:col-span-full"
          ref={riveWrapperRef}
        >
          <RiveComponent className={clsx('relative pointer-events-none', className)} />
        </div>
      </div>
    </section>
  );
};

HeroWithTagAndAnimation.propTypes = {
  tag: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonUrl: PropTypes.string.isRequired,
  linkText: PropTypes.string.isRequired,
  linkUrl: PropTypes.string.isRequired,
  animationName: PropTypes.oneOf(Object.keys(riveAnimations)).isRequired,
};

export default HeroWithTagAndAnimation;
